import {
  EssayQuestion,
  FillInBlankQuestion,
  Form,
  FormCategory,
  FormCategoryStatus,
  FormStatus,
  Heading,
  MatchingQuestion,
  MultipleChoiceQuestion,
  PreviousQuestionId,
  PreviousSectionId,
  Question,
  QuestionProps,
  QuestionType,
  Section,
  SingleChoiceQuestion,
  Tag,
} from '@module/form';
import { UniqueEntityID } from '@digibricks/domains';
import { AiChangeType } from '@module/ai';
import type {
  ImportFormDto,
  ImportHeadingDto,
  ImportQuestionDto,
  ImportSectionDto,
} from '../markdown-to-json/types';

export class FormImportDtoToEntityConverter {
  public static execute(
    formId: string,
    importDto: ImportFormDto,
  ): [Form, Question<QuestionProps>[]] {
    const folderId = importDto.folderId ?? new UniqueEntityID().toString();
    const categoryKey = importDto.category ?? 'uncategorized';

    const sections = FormImportDtoToEntityConverter.mapSections(importDto.sections, formId);
    const headings = FormImportDtoToEntityConverter.mapHeadings(importDto.headings, formId);
    const tags = importDto.tags ? FormImportDtoToEntityConverter.mapTags(importDto.tags) : [];
    const category = FormCategory.create({
      id: categoryKey,
      key: categoryKey,
      status: FormCategoryStatus.Active,
      possibleQuestionCreationTypes: null,
    }).getValue();

    const form = Form.create({
      id: formId,
      folderId,
      title: importDto.title,
      status: FormStatus.Active,
      description: importDto.description ?? '',
      coverImage: importDto.coverImage,
      thumbnail: importDto.thumbnail ?? '',
      createdBy: importDto.createdBy,
      sections,
      headings,
      tags,
      category,
    }).getValue();

    const questions = FormImportDtoToEntityConverter.mapQuestions(importDto, formId);

    return [form, questions];
  }

  private static mapSections(sections: ImportSectionDto[], formId: string): Section[] {
    return sections.map((section) =>
      Section.create({
        id: section.id,
        title: section.title,
        description: section.description ?? '',
        formId,
        previousSectionId:
          section.previousSectionId === null || section.previousSectionId === undefined
            ? PreviousSectionId.None
            : section.previousSectionId,
        aiChangeType: AiChangeType.Add,
      }).getValue(),
    );
  }

  private static mapHeadings(headings: ImportHeadingDto[], formId: string): Heading[] {
    return headings.map((heading) =>
      Heading.create(
        {
          title: heading.title,
          description: heading.description ?? '',
          formId,
          sectionId: heading.sectionId,
          nextQuestionId:
            heading.nextQuestionId === null || heading.nextQuestionId === undefined
              ? PreviousQuestionId.none.id.toString()
              : heading.nextQuestionId,
          previousQuestionId:
            heading.previousQuestionId === null || heading.previousQuestionId === undefined
              ? PreviousQuestionId.none.id.toString()
              : heading.previousQuestionId,
          nextHeadingId: heading.nextHeadingId ?? PreviousQuestionId.none.id.toString(),
          previousHeadingId: heading.previousHeadingId ?? PreviousQuestionId.none.id.toString(),
          aiChangeType: AiChangeType.Add,
        },
        new UniqueEntityID(heading.id).toString(),
      ).getValue(),
    );
  }

  private static mapTags(tagKeys: string[]): Tag[] {
    return tagKeys.map((key) =>
      Tag.create({
        id: new UniqueEntityID().toString(),
        key,
      }).getValue(),
    );
  }

  private static mapQuestions(importDto: ImportFormDto, formId: string): Question<QuestionProps>[] {
    const questions: Question<QuestionProps>[] = [];

    for (const section of importDto.sections) {
      for (const question of section.questions) {
        questions.push(FormImportDtoToEntityConverter.mapQuestion(question, formId, section.id));
      }
    }

    return questions;
  }

  private static mapQuestion(
    question: ImportQuestionDto,
    formId: string,
    sectionId: string,
  ): Question<QuestionProps> {
    const baseData = {
      ...question,
      formId,
      sectionId,
      files: question.files ?? [],
      previousQuestionId:
        question.previousQuestionId === null || question.previousQuestionId === undefined
          ? PreviousQuestionId.none.id.toString()
          : question.previousQuestionId,
      nextQuestionId:
        question.nextQuestionId === null || question.nextQuestionId === undefined
          ? PreviousQuestionId.none.id.toString()
          : question.nextQuestionId,
      aiChangeType: AiChangeType.Add,
    };

    if (question.type === QuestionType.FillInBlank) {
      const fillInBlankData = {
        ...baseData,
        correctAnswers: (question.correctAnswers ?? []).map((correctAnswer, index) => ({
          id: correctAnswer.id,
          answerId: correctAnswer.answerId,
          score: correctAnswer.score ?? 1,
          index: correctAnswer.index ?? index,
        })),
      };
      return FillInBlankQuestion.fromJson(fillInBlankData).getValue();
    }

    switch (question.type) {
      case QuestionType.Essay:
        return EssayQuestion.fromJson(baseData).getValue();
      case QuestionType.SingleChoice:
        return SingleChoiceQuestion.fromJson(baseData).getValue();
      case QuestionType.MultipleChoice:
        return MultipleChoiceQuestion.fromJson(baseData).getValue();
      case QuestionType.Matching:
        return MatchingQuestion.fromJson(baseData).getValue();
      default:
        return EssayQuestion.fromJson(baseData).getValue();
    }
  }
}
